import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import * as React from 'react';
import { createPopper } from '@popperjs/core';
import Box from '../Box';
import Portal from '../Portal';
import { lockScroll } from '../utils';
import rewireTabOrder from './rewireTabOrder';
import { getFixedParent, getModifiers } from './utils';
import { useThemeProps } from '../system';
export var Popover = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var anchorElement = _ref.anchorElement,
      _ref$as = _ref.as,
      Element = _ref$as === void 0 ? Box : _ref$as,
      innerRef = _ref.innerRef,
      _ref$keepInView = _ref.keepInView,
      keepInView = _ref$keepInView === void 0 ? true : _ref$keepInView,
      lockScrollWhileOpen = _ref.lockScrollWhileOpen,
      matchWidths = _ref.matchWidths,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? 8 : _ref$offset,
      open = _ref.open,
      onKeyDown = _ref.onKeyDown,
      onRequestClose = _ref.onRequestClose,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'bottom' : _ref$placement,
      relativeTo = _ref.relativeTo,
      renderTo = _ref.renderTo,
      _ref$shouldFlip = _ref.shouldFlip,
      shouldFlip = _ref$shouldFlip === void 0 ? false : _ref$shouldFlip,
      restProps = _objectWithoutPropertiesLoose(_ref, ["anchorElement", "as", "innerRef", "keepInView", "lockScrollWhileOpen", "matchWidths", "offset", "open", "onKeyDown", "onRequestClose", "placement", "relativeTo", "renderTo", "shouldFlip"]);

  var _useThemeProps = useThemeProps('popover', restProps),
      _useThemeProps$zIndex = _useThemeProps.zIndex,
      zIndex = _useThemeProps$zIndex === void 0 ? 10000 : _useThemeProps$zIndex,
      themeProps = _objectWithoutPropertiesLoose(_useThemeProps, ["zIndex"]);

  var anchorRef = React.useRef(null);
  var popperRef = React.useRef(null);
  var unlockScroll = React.useRef(null);
  var unwireTabOrder = React.useRef(null);

  var _React$useState = React.useState(renderTo),
      renderPopperTo = _React$useState[0],
      setRenderPopperTo = _React$useState[1];

  var setPopperInnerRef = function setPopperInnerRef(popperNode) {
    if (popperNode) {
      if (popperRef.current === null) {
        var anchorNode = relativeTo ? relativeTo.current || relativeTo : anchorRef.current;
        popperRef.current = createPopper(anchorNode, popperNode, {
          placement: placement,
          // PopperJS is written in Flow and TS isn't playing nice :(
          // @ts-ignore
          modifiers: getModifiers({
            matchWidths: matchWidths,
            offset: offset,
            keepInView: keepInView,
            shouldFlip: shouldFlip
          })
        });

        if (unwireTabOrder.current) {
          unwireTabOrder.current();
        }

        unwireTabOrder.current = rewireTabOrder(popperNode);

        if (lockScrollWhileOpen) {
          if (unlockScroll.current) {
            unlockScroll.current();
          }

          unlockScroll.current = lockScroll(popperNode);
        }
      }
    } else {
      cleanup();
    }

    if (typeof innerRef === 'function') {
      innerRef(popperNode);
    } else if (innerRef != null && innerRef.current) {
      innerRef.current = popperNode;
    }
  };

  var cleanup = function cleanup() {
    if (popperRef.current) {
      popperRef.current.destroy();
      popperRef.current = null;
    }

    if (unwireTabOrder.current) {
      unwireTabOrder.current();
      unwireTabOrder.current = null;
    }

    if (unlockScroll.current) {
      unlockScroll.current();
      unlockScroll.current = null;
    }
  };

  var requestClose = function requestClose(event) {
    if (event.key === 'Escape' && onRequestClose) {
      if (open) {
        event.nativeEvent.stopImmediatePropagation();
      }

      onRequestClose();
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  }; // cleanup when open has changed to `false` and on component unmount


  React.useEffect(function () {
    return function () {
      if (open === false) {
        cleanup();
      }
    };
  }, [open]); // reposition the popover if an update in React occured while open
  // for instance this can happen when selecting multiple items in a menu and the anchor grows/shrinks

  React.useEffect(function () {
    if (open && popperRef.current) {
      popperRef.current.update();
    }
  }); // by default this will make sure popovers render in the same fixed area as the anchor

  React.useEffect(function () {
    if (renderPopperTo === undefined && anchorElement) {
      setRenderPopperTo(getFixedParent(anchorElement));
    }
  }, [anchorElement]); // carried over from previous implementation, used exclusively in Dropdown

  React.useImperativeHandle(ref, function () {
    return {
      focusAnchor: function focusAnchor() {
        if (anchorRef.current && anchorRef.current.focus) {
          anchorRef.current.focus();
        }
      }
    };
  });
  return /*#__PURE__*/React.createElement(Portal, {
    renderTo: renderPopperTo
  }, typeof anchorElement === 'function' ? anchorElement({
    innerRef: function innerRef(node) {
      return anchorRef.current = node;
    },
    onKeyDown: requestClose
  }) : /*#__PURE__*/React.cloneElement(anchorElement, {
    innerRef: function innerRef(node) {
      var _anchorElement$props$;

      anchorRef.current = node;

      if (typeof anchorElement.props.innerRef === 'function') {
        anchorElement.props.innerRef(node);
      } else if ((_anchorElement$props$ = anchorElement.props.innerRef) != null && _anchorElement$props$.current) {
        anchorElement.props.innerRef.current = node;
      }
    },
    onKeyDown: function onKeyDown(event) {
      requestClose(event);

      if (anchorElement.props.onKeyDown) {
        anchorElement.props.onKeyDown(event);
      }
    }
  }), open ? /*#__PURE__*/React.createElement(Element, _objectSpread({
    innerRef: setPopperInnerRef,
    width: 'auto',
    onKeyDown: function (_onKeyDown) {
      function onKeyDown(_x) {
        return _onKeyDown.apply(this, arguments);
      }

      onKeyDown.toString = function () {
        return _onKeyDown.toString();
      };

      return onKeyDown;
    }(function (event) {
      requestClose(event);

      if (onKeyDown) {
        onKeyDown(event);
      }
    }),
    // ideally this should be pulled out to something like ThemeProvider for predictable z indices
    style: {
      zIndex: zIndex
    }
  }, themeProps)) : null);
});
Popover.displayName = 'Popover';